import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { EmpresaProvider } from './contexts/EmpresaContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import PrivateRoute from './components/PrivateRoute';
import FeatureRoute from './components/FeatureRoute';
import ClientLayout from './layouts/ClientLayout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Assinaturas from './pages/Assinaturas';
import AssinaturaDetalhes from './pages/AssinaturaDetalhes';
import FaturaDetalhes from './pages/FaturaDetalhes';
import Colaboradores from './pages/Colaboradores';
import ColaboradorFicha from './pages/ColaboradorFicha';
import Solicitantes from './pages/Solicitantes';
import Faturamento from './pages/Faturamento';
import ESocial from './pages/ESocial';
import ComplianceRegulamentacao from './pages/ComplianceRegulamentacao';
import VencimentosExames from './pages/VencimentosExames';
import CandidatosAprovacao from './pages/CandidatosAprovacao';
import AgendamentoCadastro from './pages/Agendamento/Cadastro';
import AgendamentoAgendamento from './pages/Agendamento/Agendamento';
import AgendamentoDetalhes from './pages/Agendamento/Detalhes';
import AgendamentoResumo from './pages/Agendamento/Resumo';
import AgendamentoFinal from './pages/Agendamento/Final';
// import Pesquisas from './pages/Pesquisas';
// import PesquisasEmpresas from './pages/PesquisasEmpresas';
// import PesquisaEmpresaDetalhes from './pages/PesquisaEmpresaDetalhes';
// import PlanoAcaoPage from './pages/Pesquisas/PlanoAcao/[pesquisaEmpresaId]';
import FeaturesDebug from './pages/FeaturesDebug';
import Profile from './pages/Profile';
import Resultados from './pages/Resultados';
import PortalColaborador from './pages/PortalColaborador';
import ConfirmarPresenca from './pages/ConfirmarPresenca';
import AvaliacaoExperiencia from './pages/AvaliacaoExperiencia';
import OrganizacaoPage from './pages/OrganizacaoPage';
import GestaoEpisPage from './pages/Epis/GestaoEpisPage';
import CipaClientePage from './pages/Cipa/CipaClientePage';
import EpiAceitePublico from './pages/Epis/EpiAceitePublico';
import Integracoes from './pages/Integracoes';
import GestaoAtestadosPage from './pages/Saude/GestaoAtestadosPage';
import SaudeCorporativaDashboardPage from './pages/Saude/SaudeCorporativaDashboardPage';
import EnvironmentBanner from './components/EnvironmentBanner';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const queryClient = new QueryClient();

function App() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const pathname = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
  const isColabSubdomain =
    hostname.startsWith('colab.') ||
    hostname.includes('colab.sstlocal') ||
    hostname.includes('colab.staging');
  const isPortalColaboradorRoute =
    !pathname.startsWith('/colaboradores') &&
    (pathname.startsWith('/escolha-clinica') ||
      pathname === '/colaborador' ||
      pathname.startsWith('/colaborador/') ||
      pathname === '/colab' ||
      pathname.startsWith('/colab/') ||
      pathname.startsWith('/portal-colaborador') ||
      pathname.startsWith('/confirmar-presenca') ||
      pathname.startsWith('/presenca') ||
      pathname.startsWith('/avaliar') ||
      pathname.startsWith('/feedback') ||
      pathname.startsWith('/epi/aceite'));

  // Subdomínio colab.sstexames / colab.staging / colab.sstlocal OU rota pública do Portal do Colaborador (/escolha-clinica, etc.)
  // Renderiza apenas EnvironmentBanner e PortalColaborador SEM AuthProvider, EmpresaProvider, SidebarProvider ou TimezoneProvider
  if (isColabSubdomain || isPortalColaboradorRoute) {
    return (
      <QueryClientProvider client={queryClient}>
        <EnvironmentBanner />
        <ErrorBoundary>
          <Router>
            <Routes>
              <Route path="/" element={<PortalColaborador />} />
              <Route path="/:token" element={<PortalColaborador />} />
              <Route path="/escolha-clinica" element={<PortalColaborador />} />
              <Route path="/escolha-clinica/:token" element={<PortalColaborador />} />
              <Route path="/colaborador" element={<PortalColaborador />} />
              <Route path="/colaborador/:token" element={<PortalColaborador />} />
              <Route path="/colab" element={<PortalColaborador />} />
              <Route path="/colab/:token" element={<PortalColaborador />} />
              <Route path="/portal-colaborador" element={<PortalColaborador />} />
              <Route path="/confirmar-presenca/:token" element={<ConfirmarPresenca />} />
              <Route path="/confirmar-presenca" element={<ConfirmarPresenca />} />
              <Route path="/presenca/:token" element={<ConfirmarPresenca />} />
              <Route path="/presenca" element={<ConfirmarPresenca />} />
              <Route path="/avaliar/:token" element={<AvaliacaoExperiencia />} />
              <Route path="/avaliar" element={<AvaliacaoExperiencia />} />
              <Route path="/feedback/:token" element={<AvaliacaoExperiencia />} />
              <Route path="/feedback" element={<AvaliacaoExperiencia />} />
              <Route path="/epi/aceite/:token" element={<EpiAceitePublico />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </ErrorBoundary>
      </QueryClientProvider>
    );
  }

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <EnvironmentBanner />
        <ErrorBoundary>
          <AuthProvider>
          <EmpresaProvider>
            <TimezoneProvider>
                <Router>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/change-password" element={<ChangePassword />} />
                    <Route path="/colaborador" element={<PortalColaborador />} />
                    <Route path="/colaborador/:token" element={<PortalColaborador />} />
                    <Route path="/colab" element={<PortalColaborador />} />
                    <Route path="/colab/:token" element={<PortalColaborador />} />
                    <Route path="/portal-colaborador" element={<PortalColaborador />} />
                    <Route path="/escolha-clinica" element={<PortalColaborador />} />
                    <Route path="/escolha-clinica/:token" element={<PortalColaborador />} />
                    <Route path="/confirmar-presenca/:token" element={<ConfirmarPresenca />} />
                    <Route path="/confirmar-presenca" element={<ConfirmarPresenca />} />
                    <Route path="/presenca/:token" element={<ConfirmarPresenca />} />
                    <Route path="/presenca" element={<ConfirmarPresenca />} />
                    <Route path="/avaliar/:token" element={<AvaliacaoExperiencia />} />
                    <Route path="/avaliar" element={<AvaliacaoExperiencia />} />
                    <Route path="/feedback/:token" element={<AvaliacaoExperiencia />} />
                    <Route path="/feedback" element={<AvaliacaoExperiencia />} />
                    <Route
                      path="/"
                      element={
                        <PrivateRoute>
                          <ClientLayout>
                            <Dashboard />
                          </ClientLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/organizacao"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_organizacional" featureName="Gestão Organizacional">
                            <ClientLayout>
                              <OrganizacaoPage />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/arvore-organizacional"
                      element={<Navigate to="/organizacao" replace />}
                    />

                    <Route
                      path="/assinaturas"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="financeiro" featureName="Assinaturas">
                            <ClientLayout>
                              <Assinaturas />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/assinaturas/:id"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="financeiro" featureName="Assinaturas">
                            <ClientLayout>
                              <AssinaturaDetalhes />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/assinaturas/:subscriptionId/faturas/:invoiceId"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="financeiro" featureName="Assinaturas">
                            <ClientLayout>
                              <FaturaDetalhes />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/colaboradores"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_organizacional" requiredFeature="colaboradores" featureName="Colaboradores">
                            <ClientLayout>
                              <Colaboradores />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/colaboradores/:id/ficha"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_organizacional" requiredFeature="colaboradores" featureName="Colaboradores">
                            <ClientLayout>
                              <ColaboradorFicha />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/epis"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_epis" requiredFeature="epis" featureName="Gestão de EPIs">
                            <ClientLayout>
                              <GestaoEpisPage />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/cipa"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_cipa" featureName="CIPA & Comissões (NR-5)">
                            <ClientLayout>
                              <CipaClientePage />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/saude/atestados"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_absenteismo" featureName="Gestão de Absenteísmo">
                            <ClientLayout>
                              <GestaoAtestadosPage />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/saude/dashboard"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="saude_corporativa" featureName="Saúde Corporativa & PCMSO">
                            <ClientLayout>
                              <SaudeCorporativaDashboardPage />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/solicitantes"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_organizacional" requiredFeature="solicitantes" featureName="Solicitantes">
                            <ClientLayout>
                              <Solicitantes />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/faturamento"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="financeiro" requiredFeature="faturamento" featureName="Faturamento">
                            <ClientLayout>
                              <Faturamento />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/esocial"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_compliance" requiredFeature="esocial" featureName="E-social">
                            <ClientLayout>
                              <ESocial />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/compliance"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_compliance" featureName="Compliance & Regulamentação">
                            <ClientLayout>
                              <ComplianceRegulamentacao />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/programas-laudos"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_compliance" featureName="Programas e Laudos">
                            <ClientLayout>
                              <ComplianceRegulamentacao />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/autorizacoes-outorgas"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_compliance" featureName="Autorizações e Outorgas">
                            <ClientLayout>
                              <ComplianceRegulamentacao />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/vencimentos-exames"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_exames" requiredFeature="vencimentos_exames" featureName="Vencimentos de Exames">
                            <ClientLayout>
                              <VencimentosExames />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/candidatos-aprovacao"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_organizacional" featureName="Aprovação de Candidatos">
                            <ClientLayout>
                              <CandidatosAprovacao />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/agendamento"
                      element={<Navigate to="/agendamento/cadastro" replace />}
                    />
                    {/* Novo fluxo de agendamento (igual ao scheduling) */}
                    <Route
                      path="/agendamento/cadastro"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_exames" requiredFeature="exames" featureName="Agendamento de Exames">
                            <ClientLayout>
                              <AgendamentoCadastro />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/agendamento/agendamento"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_exames" requiredFeature="exames" featureName="Agendamento de Exames">
                            <ClientLayout>
                              <AgendamentoAgendamento />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/agendamento/detalhes"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_exames" requiredFeature="exames" featureName="Agendamento de Exames">
                            <ClientLayout>
                              <AgendamentoDetalhes />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/agendamento/resumo"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_exames" requiredFeature="exames" featureName="Agendamento de Exames">
                            <ClientLayout>
                              <AgendamentoResumo />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/agendamento/final/:codigo"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_exames" requiredFeature="exames" featureName="Agendamento de Exames">
                            <ClientLayout>
                              <AgendamentoFinal />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    {/* Rota antiga de agendamento (redirecionar para o fluxo novo) */}
                    <Route
                      path="/exames/agendamento"
                      element={<Navigate to="/agendamento/cadastro" replace />}
                    />
                    <Route
                      path="/resultados"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="gestao_exames" requiredFeature="exames" featureName="Resultados">
                            <ClientLayout>
                              <Resultados />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    {/* NR-01 / Pesquisas descontinuadas */}
                    {/* <Route
                      path="/pesquisas"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredFeature="nr1_pesquisas" featureName="Pesquisas">
                            <ClientLayout>
                              <Pesquisas />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/pesquisas-empresas"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredFeature="nr1_pesquisas" featureName="Pesquisas">
                            <ClientLayout>
                              <PesquisasEmpresas />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/pesquisas-empresas/:id"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredFeature="nr1_pesquisas" featureName="Pesquisas">
                            <ClientLayout>
                              <PesquisaEmpresaDetalhes />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/pesquisas/plano-acao/:pesquisaEmpresaId"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredFeature="nr1_pesquisas" featureName="Pesquisas">
                            <ClientLayout>
                              <PlanoAcaoPage />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    /> */}
                    <Route
                      path="/profile"
                      element={
                        <PrivateRoute>
                          <ClientLayout>
                            <Profile />
                          </ClientLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/integracoes"
                      element={
                        <PrivateRoute>
                          <FeatureRoute requiredModulo="integracoes" featureName="Integrações & API">
                            <ClientLayout>
                              <Integracoes />
                            </ClientLayout>
                          </FeatureRoute>
                        </PrivateRoute>
                      }
                    />
                    {/* Rota de debug de features - apenas em desenvolvimento */}
                    {import.meta.env.DEV && (
                      <Route
                        path="/features-debug"
                        element={
                          <PrivateRoute>
                            <ClientLayout>
                              <FeaturesDebug />
                            </ClientLayout>
                          </PrivateRoute>
                        }
                      />
                    )}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Router>
              </TimezoneProvider>
          </EmpresaProvider>
          </AuthProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </>
  );
}

export default App;
